import { useState, useRef, useEffect, useMemo } from "react";
import { motion, useMotionValue, useTransform } from "framer-motion";
import Hls from "hls.js";
import logoWhite from "@/assets/logo-white.svg";
import AnimatedMenuButton from "@/components/ui/AnimatedMenuButton";
import FullPageMenu from "@/components/FullPageMenu";
import EarlyAccessModal from "@/components/EarlyAccessModal";
import VolumeToggle from "@/components/ui/VolumeToggle";

const HERO_VIDEO_URL = "https://customer-izud1idylkfqkoa2.cloudflarestream.com/58bd12339bafa1040d9b189e120f6f57/manifest/video.m3u8";
const HERO_POSTER_URL = "https://customer-izud1idylkfqkoa2.cloudflarestream.com/58bd12339bafa1040d9b189e120f6f57/thumbnails/thumbnail.jpg";

const navLinks = [
  { label: "Lineup", href: "/lineup" },
  { label: "Guide", href: "/guide" },
  { label: "Lifestyle", href: "/lifestyle" },
  { label: "Contact", href: "/contact" },
];

// Cursor-following nav underline component - optimized with useMotionValue
const SlidingNav = ({ links }: { links: { label: string; href: string; comingSoon?: boolean }[] }) => {
  const navRef = useRef<HTMLDivElement>(null);
  const lineX = useMotionValue(0);
  const [isVisible, setIsVisible] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const navRect = navRef.current?.getBoundingClientRect();
    if (navRect) {
      const x = e.clientX - navRect.left;
      lineX.set(x);
    }
  };

  // Always 80px when inside the nav band (between the two lines)
  const lineLeft = useTransform(lineX, (x) => x - 40);

  return (
    <div
      ref={navRef}
      className="hidden md:block relative"
      onMouseMove={handleMouseMove}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {/* Links stretch full height from line to line */}
      <nav className="flex items-stretch justify-center gap-0">
        {links.map((link) => {
          if (link.comingSoon) {
            return (
              <span
                key={link.label}
                className="text-white/30 text-sm font-medium tracking-widest uppercase px-8 py-4 flex items-center cursor-not-allowed"
              >
                {link.label}
              </span>
            );
          }
          return (
            <a
              key={link.label}
              href={link.href}
              className="text-white/70 hover:text-white transition-colors text-sm font-medium tracking-widest uppercase px-8 py-4 flex items-center"
            >
              {link.label}
            </a>
          );
        })}
      </nav>
      
      {/* Cursor-following line - always 80px when in the nav band */}
      <motion.span
        className="absolute bottom-0 h-px bg-white pointer-events-none"
        style={{
          left: lineLeft,
          width: 80,
          opacity: isVisible ? 1 : 0,
          transition: "opacity 0.15s ease-out",
        }}
      />
    </div>
  );
};


const Hero = () => {
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [earlyAccessOpen, setEarlyAccessOpen] = useState(false);
  const [stickyVisible, setStickyVisible] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const lastScrollY = useRef(0);

  const toggleMute = () => {
    if (videoRef.current) {
      videoRef.current.muted = !videoRef.current.muted;
      setIsMuted(videoRef.current.muted);
    }
  };

  // Sticky nav show/hide logic
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      const heroHeight = heroRef.current?.offsetHeight || window.innerHeight;
      const isInHero = currentScrollY < heroHeight;
      const isScrollingUp = currentScrollY < lastScrollY.current;

      if (isInHero || isScrollingUp) {
        setStickyVisible(true);
      } else {
        setStickyVisible(false);
      }

      lastScrollY.current = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Listen for early access modal trigger from other components
  useEffect(() => {
    const handleOpenEarlyAccess = () => setEarlyAccessOpen(true);
    window.addEventListener('openEarlyAccess', handleOpenEarlyAccess);
    return () => window.removeEventListener('openEarlyAccess', handleOpenEarlyAccess);
  }, []);

  // Initialize HLS streaming
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let hls: Hls | null = null;

    if (Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hls.loadSource(HERO_VIDEO_URL);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        video.play().catch(() => {});
      });
    } else if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = HERO_VIDEO_URL;
      video.addEventListener('loadedmetadata', () => {
        video.play().catch(() => {});
      });
    }

    return () => {
      if (hls) {
        hls.destroy();
      }
    };
  }, []);

  return (
    <section ref={heroRef} className="relative min-h-screen overflow-x-hidden overflow-y-visible">
      {/* Full Page Menu */}
      <FullPageMenu 
        isOpen={menuOpen} 
        onClose={() => setMenuOpen(false)} 
        onJoinWaitlist={() => setEarlyAccessOpen(true)} 
      />
      <EarlyAccessModal 
        isOpen={earlyAccessOpen} 
        onClose={() => setEarlyAccessOpen(false)} 
      />

      {/* STICKY: Only Menu + Get Tickets buttons */}
      <div 
        className={`fixed top-0 left-0 right-0 z-50 pointer-events-none ${
          stickyVisible && !menuOpen
            ? 'translate-y-0 opacity-100' 
            : '-translate-y-8 opacity-0'
        }`}
        style={{
          transition: stickyVisible && !menuOpen
            ? 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.3s ease-out'
            : 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.5s ease-in-out'
        }}
      >
        <div className="w-full px-6 md:px-12 lg:px-16 py-8 md:py-6 flex items-center justify-between">
          <div className="pointer-events-auto">
            <AnimatedMenuButton onClick={() => setMenuOpen(true)} />
          </div>
          
          <div className="pointer-events-auto">
            <a
              href="/tickets"
              className="btn-primary px-7 py-3 md:px-8 md:py-3.5 text-base font-semibold"
            >
              Get Tickets
            </a>
          </div>
        </div>
      </div>
      
      {/* Fallback Background Image */}
      <div 
        className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500 ${videoLoaded ? 'opacity-0' : 'opacity-100'}`}
        style={{ backgroundImage: `url(${HERO_POSTER_URL})` }}
      />
      
      {/* Video Background */}
      <video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        poster={HERO_POSTER_URL}
        onLoadedData={() => setVideoLoaded(true)}
        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ${videoLoaded ? 'opacity-100' : 'opacity-0'}`}
      />
      
      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" />
      
      {/* NON-STICKY: Full Header with Logo, Dates, Nav - stays at top, scrolls away */}
      <header className="absolute top-0 left-0 right-0 z-20">
        <div>
          <div className="w-full px-6 md:px-12 lg:px-16 py-8 md:py-6">
            {/* Top row: Menu | Logo | Get Tickets */}
            <div className="flex items-center justify-between relative">
              {/* Invisible spacer for menu button */}
              <div className="flex-shrink-0 opacity-0 pointer-events-none">
                <AnimatedMenuButton onClick={() => {}} />
              </div>
              
              {/* Logo with flanking dates - center (desktop only) */}
              <div className="hidden md:flex items-center justify-center gap-4 md:gap-8 absolute left-1/2 -translate-x-1/2">
                <span className="text-white/70 text-xs md:text-sm font-light tracking-[0.2em] w-16 md:w-20 text-right">JULY 17-18</span>
                <a href="/" className="block">
                  <img src={logoWhite} alt="Yacht Rock Detroit" className="h-24 md:h-32 w-auto" />
                </a>
                <span className="text-white/70 text-xs md:text-sm font-light tracking-[0.2em] w-16 md:w-20 text-left">2026</span>
              </div>
              
              {/* Invisible spacer for Join Waitlist */}
              <div className="flex-shrink-0 opacity-0 pointer-events-none">
                <span className="btn-primary px-5 py-2.5 md:px-6 md:py-2 text-sm font-semibold">
                  Get Tickets
                </span>
              </div>
            </div>
          </div>
          
          {/* Divider line */}
          <div className="h-px bg-white/20" />
          
          {/* Navigation row - desktop only, full width for hover animation */}
          <div className="w-full">
            <SlidingNav links={navLinks} />
          </div>
          
          {/* Bottom divider line */}
          <div className="h-px bg-white/20" />
        </div>
      </header>

      {/* Main Hero Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center px-6 pt-40 md:pt-32 pb-24">
        <div className="max-w-5xl mx-auto text-center">
          {/* Logo - mobile only */}
          <div className="md:hidden mb-8 animate-fade-up">
            <img src={logoWhite} alt="Yacht Rock Detroit" className="h-20 w-auto mx-auto" />
          </div>
          
          {/* Headline */}
          <h1 
            className="text-white font-bold leading-tight mb-4 animate-fade-up"
          >
            <span className="block text-4xl sm:text-5xl md:text-5xl lg:text-6xl" style={{ fontFamily: 'Georgia, Times New Roman, serif', fontWeight: 300 }}>Yacht Rock Detroit</span>
            <span className="block text-5xl sm:text-6xl md:text-7xl lg:text-8xl italic uppercase tracking-wide mt-2">Tickets on Sale Now</span>
          </h1>

          {/* Subheadline */}
          <p className="text-white/70 text-lg md:text-xl font-light mb-10 animate-fade-up animate-delay-100" style={{ fontFamily: 'Georgia, Times New Roman, serif' }}>
            July 17–18, 2026 · A Weekend on the Detroit River
          </p>

          {/* CTA Button */}
          <div className="animate-fade-up animate-delay-200 flex flex-col items-center">
            <a
              href="/tickets"
              className="btn-primary px-12 py-5 text-xl md:text-2xl font-bold tracking-wide uppercase"
            >
              Get Tickets Now
            </a>
          </div>
        </div>
      </div>

      {/* Volume Toggle - bottom left */}
      <div className="absolute bottom-8 left-8 z-20">
        <VolumeToggle isMuted={isMuted} onClick={toggleMute} />
      </div>

    </section>
  );
};

export default Hero;
